import type { InputProps } from "@chakra-ui/react";
import { HStack, Input } from "@chakra-ui/react";
import { useState } from "react";
import { IconButton } from "components/button/IconButton";

export interface PasswordInputProps extends InputProps {
  autocomplete: "off" | "on" | "current-password" | "new-password";
  shouldStartVisible?: boolean;
}

/**
 * Password inputs allow users to securely enter sensitive credentials while
 * protecting them from accidental exposure. They balance security with
 * usability by providing controlled visibility and optional password strength
 * feedback during account creation or credential updates. For verification
 * codes use Digit Input Group; for visible values use Read-only Input.
 */
export const PasswordInput = ({
  autocomplete,
  shouldStartVisible = false,
  isDisabled,
  ...rest
}: PasswordInputProps) => {
  const [isShowing, setIsShowing] = useState<boolean>(shouldStartVisible);
  const handleToggleVisibility = () => {
    if (!isDisabled) {
      setIsShowing((showing) => !showing);
    }
  };

  return (
    <HStack spacing="2">
      <Input
        type={isShowing ? "text" : "password"}
        autoComplete={autocomplete}
        isDisabled={isDisabled}
        sx={{ fontVariantLigatures: "none" }}
        {...rest}
      />
      <IconButton
        variant="secondary"
        size="md"
        label="visibility"
        onClick={handleToggleVisibility}
        iconName={isShowing ? "actions/Unvisibility" : "actions/Visibility"}
        h="9"
        w="9"
        borderRadius="lg"
        color="fg.emphasized"
        bg="bg.container"
        border="1px solid"
        borderColor="neutral.emphasized"
        data-cy="password-reveal"
      />
    </HStack>
  );
};
